import { useCallback, useMemo } from 'react';
import type { VirtualizerOptions } from '@tanstack/virtual-core';
import { defaultRangeExtractor } from '@tanstack/virtual-core';

export const UNSUPPORTED_CHILD_NODE_TYPES = [
	'codeBlock',
	'embedCard',
	'expand',
	'nestedExpand',
	'heading',
	'extension',
	'extensionFrame',
	'bodiedExtension',
	'multiBodiedExtension',
	'inlineExtension',
];

const alwaysRangeExtractor = (childInfo: ChildInfo | undefined) => {
	return (childInfo?.childrenNodeTypeCounts || []).reduce((acc, childNodeTypeCounts, index) => {
		// Add row 0 to always include the header row. This is important because, at the time of writing,
		// adding and removing it from the DOM results in the unstable keys for the tables rows.
		// Technically, it may not be a header row. However, it is just 1 row and it is simpler to just always include it.
		const isHeaderRow = index === 0;
		// Virtualization is disabled if any unsupported child node types are present.
		const isUnsupportedRow = UNSUPPORTED_CHILD_NODE_TYPES.some(
			(nodeType) => childNodeTypeCounts[nodeType],
		);
		// Virtualization is disabled if any annotations, such as inline comments, are present.
		const isAnnotationRow = childInfo?.childrenHasAnnotations[index];
		if (isHeaderRow || isUnsupportedRow || isAnnotationRow) {
			acc.push(index);
		}

		return acc;
	}, [] as number[]);
};

const combineRanges = (rangeA: number[], rangeB: number[]): number[] => {
	const combinedRange: number[] = [];
	let rangeAIndex = 0;
	let rangeBIndex = 0;
	while (rangeAIndex < rangeA.length || rangeBIndex < rangeB.length) {
		const rangeAValue = rangeA[rangeAIndex];
		const rangeBValue = rangeB[rangeBIndex];

		if (!Number.isInteger(rangeBValue) || rangeBValue > rangeAValue) {
			// Add element because it's in range A.
			combinedRange.push(rangeAValue);
			rangeAIndex++;
		} else if (!Number.isInteger(rangeAValue) || rangeAValue > rangeBValue) {
			// Add element because it's in range B.
			combinedRange.push(rangeBValue);
			rangeBIndex++;
		} else {
			// Add element because it's in both ranges.
			combinedRange.push(rangeAValue);
			rangeAIndex++;
			rangeBIndex++;
		}
	}

	return combinedRange;
};

type RangeExtractor = NonNullable<VirtualizerOptions<Element, Element>['rangeExtractor']>;
export type ChildInfo = {
	selfNodeTypeCounts: { [nodeType: string]: number };
	childrenNodeTypeCounts: { [nodeType: string]: number }[];
	childrenHasAnnotations: boolean[];
};

export const useCustomRangeExtractor = (childInfo: ChildInfo | undefined) => {
	const alwaysRange = useMemo(() => alwaysRangeExtractor(childInfo), [childInfo]);

	return useCallback<RangeExtractor>(
		(range) => {
			const defaultRange = defaultRangeExtractor(range);
			const combinedRange = combineRanges(defaultRange, alwaysRange);
			return combinedRange;
		},
		[alwaysRange],
	);
};
